import React, { useMemo, useRef, useState } from "react";

import { useSelector } from "react-redux";
import styled from "styled-components/macro";

import Typography from "@material-ui/core/Typography";
import { Alert } from "@material-ui/lab";

import { Column } from "components/Layout";
import SearchInputTable from "components/SearchInput";

import { space } from "constants/theme";

import { openEditBusinessModal } from "lib/navigation";

import { getBusinesses, getSideLoadedBusinesses } from "selectors";

import { TemporaryBusiness } from "components/Business/TemporaryBusiness";
import { useConnectedBusinessMatchesById } from "components/Business/TemporaryBusiness/useConnectedBusinessMatches";
import { useTemporaryBusinessMatcher } from "components/Business/TemporaryBusiness/useTemporaryBusinessMatcher";

import { TemporaryBusinessIntro } from "./TemporaryBusinessIntro";
import { useTemporaryBusinessSearch } from "./useTemporaryBusinessSearch";

import { ResolvedBusiness } from "./ResolvedBusiness";

const SearchBox = styled(SearchInputTable)`
  margin-left: 0;
  margin-bottom: 12px;
  flex: none;
  align-self: flex-start;
`;

interface TemporaryBusinessesProps {
  temporaryBusinesses: Business[];
  onMatchedChange?: (businessId: string, isMatched: boolean | null) => void;
}

export function TemporaryBusinesses({
  temporaryBusinesses,
  onMatchedChange,
}: TemporaryBusinessesProps) {
  const matchBusiness = useTemporaryBusinessMatcher();
  const connectedMatchesById =
    useConnectedBusinessMatchesById(temporaryBusinesses);

  // Group each business once per view, so resolving one doesn't move others
  // mid-review. Without a local match, wait for the Connected search.
  const hasMatchByBusinessId = useRef<Map<string, boolean>>(new Map());

  const { foundMatches: allFoundMatches, unmatched: allUnmatched } =
    useMemo(() => {
      const found: Business[] = [];
      const none: Business[] = [];

      temporaryBusinesses.forEach(temporaryBusiness => {
        if (!hasMatchByBusinessId.current.has(temporaryBusiness.id)) {
          const connectedMatches = connectedMatchesById[temporaryBusiness.id];
          if (matchBusiness(temporaryBusiness).length) {
            hasMatchByBusinessId.current.set(temporaryBusiness.id, true);
          } else if (connectedMatches) {
            hasMatchByBusinessId.current.set(
              temporaryBusiness.id,
              connectedMatches.length > 0,
            );
          }
        }

        (hasMatchByBusinessId.current.get(temporaryBusiness.id)
          ? found
          : none
        ).push(temporaryBusiness);
      });

      return { foundMatches: found, unmatched: none };
    }, [temporaryBusinesses, matchBusiness, connectedMatchesById]);

  // Businesses resolved while this view is open move to their own section
  // rather than disappearing - merged ones leave the store, so keep a copy.
  const businesses: Record<string, Business> = useSelector(getBusinesses);
  const sideLoadedBusinesses: Record<string, SideLoadedBusiness> = useSelector(
    getSideLoadedBusinesses,
  );
  const seenById = useRef<Map<string, Business>>(new Map());
  const resolvedIds = useRef<string[]>([]);
  const mergeTargetById = useRef<Map<string, { id: string; name: string }>>(
    new Map(),
  );

  const currentIds = new Set(temporaryBusinesses.map(business => business.id));
  // Back in the list (eg unmerged) means it's no longer resolved.
  resolvedIds.current = resolvedIds.current.filter(id => {
    if (currentIds.has(id)) {
      mergeTargetById.current.delete(id);
      return false;
    }
    return true;
  });
  seenById.current.forEach((business, id) => {
    // Still temporary means it only dropped out of this list (eg it's no
    // longer used in the Sale), not that it was resolved.
    if (
      !currentIds.has(id) &&
      !resolvedIds.current.includes(id) &&
      !businesses[id]?.isTemporary
    ) {
      resolvedIds.current.unshift(id);
    }
  });
  temporaryBusinesses.forEach(business =>
    seenById.current.set(business.id, business),
  );

  const resolved = resolvedIds.current.map(id => {
    const business = businesses[id];
    const mergeTarget = mergeTargetById.current.get(id);
    let resolution = "Merged";
    if (business) {
      // A temporary Business is never linked to legacy data, so a link means
      // it was created from it.
      const isActivatedWithLegacyData = Object.values(
        sideLoadedBusinesses,
      ).some(record => record.businessIds.includes(id));
      if (!business.isActive) {
        resolution = "Inactivated";
      } else if (isActivatedWithLegacyData) {
        resolution = "Created with Legacy Data";
      } else {
        resolution = "Activated";
      }
    } else if (mergeTarget) {
      resolution = `Merged into ${mergeTarget.name}`;
    }
    return {
      id,
      name: seenById.current.get(id)?.name ?? "",
      resolution,
      // The Business it became - itself, or the one it was merged into.
      resultingBusinessId: business ? id : mergeTarget?.id,
    };
  });

  const renderTemporaryBusiness = (business: Business) => (
    <TemporaryBusiness
      key={business.id}
      business={business}
      onMatchedChange={onMatchedChange}
      onMerge={target => mergeTargetById.current.set(business.id, target)}
    />
  );

  const resolvedSection = resolved.length > 0 && (
    <>
      <Typography
        variant="h6"
        data-tour="temporary-businesses-section-resolved"
        style={{ marginTop: space[3] }}
      >
        {`Resolved Businesses (${resolved.length})`}
      </Typography>
      {resolved.map(({ id, name, resolution, resultingBusinessId }) => (
        <ResolvedBusiness
          key={id}
          name={name}
          resolution={resolution}
          onClick={
            resultingBusinessId
              ? () => openEditBusinessModal(resultingBusinessId)
              : undefined
          }
        />
      ))}
    </>
  );

  // Only filters what's shown - the grouping above still covers every card.
  const [searchText, setSearchText] = useState("");
  const matchesSearch = useTemporaryBusinessSearch(searchText);
  const foundMatches = allFoundMatches.filter(matchesSearch);
  const unmatched = allUnmatched.filter(matchesSearch);

  if (!temporaryBusinesses.length) {
    return (
      <Column fullWidth padding={2} gridGap={0}>
        <Alert severity="success">
          All temporary businesses have been resolved.
        </Alert>
        {resolvedSection}
      </Column>
    );
  }

  return (
    <Column fullWidth padding={2} gridGap={0}>
      <TemporaryBusinessIntro />
      <SearchBox
        handleChange={setSearchText}
        value={searchText}
        placeholder="Quicksearch"
      />
      <Typography
        variant="h6"
        data-tour="temporary-businesses-section-found-matches"
      >
        {`Found Matches (${foundMatches.length})`}
      </Typography>
      {foundMatches.length ? (
        foundMatches.map(renderTemporaryBusiness)
      ) : (
        <Typography color="textSecondary">
          {searchText
            ? "No temporary businesses with a suggested match fit your search."
            : "No temporary businesses currently have a suggested match."}
        </Typography>
      )}

      <Typography
        variant="h6"
        data-tour="temporary-businesses-section-unmatched"
        style={{ marginTop: space[3] }}
      >
        {`Unmatched Businesses (${unmatched.length})`}
      </Typography>
      {unmatched.length ? (
        unmatched.map(renderTemporaryBusiness)
      ) : (
        <Typography color="textSecondary">
          {searchText
            ? "No unmatched temporary businesses fit your search."
            : "Every temporary business has a suggested match."}
        </Typography>
      )}

      {resolvedSection}
    </Column>
  );
}
